import { mathsHelper } from "../../../helpers";
import { chartConstants } from "./ChartConstants";
import { useEffect, useRef } from "react";

interface ComparisonBarProps {
  label: string;
  value: number;
  total: number;
  color: string;
  onClick?: () => void;
  isClickable: boolean;
}

function ComparisonBar({
  label,
  value,
  total,
  color,
  onClick,
  isClickable,
}: ComparisonBarProps) {
  const percentage = mathsHelper.getPercentageForDisplay(value, total, 0);
  const width = (value / total) * 100;
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-width");
          }
        });
      },
      { threshold: 0.1 }
    );

    if (barRef.current) {
      observer.observe(barRef.current);
    }

    return () => {
      if (barRef.current) {
        observer.unobserve(barRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`group ${isClickable ? "cursor-pointer" : "cursor-default"}`}
      onClick={isClickable ? onClick : undefined}
    >
      <div className="flex items-center mb-4">
        <div className="w-24 text-sm text-gray-600 group-hover:font-medium transition-all duration-200">
          {label}
        </div>
        <div className="flex-1 ml-4">
          <div className="h-6 rounded-full bg-gray-100 overflow-hidden">
            <div
              ref={barRef}
              className="h-full rounded-full w-0 group-hover:shadow-md group-hover:scale-[1.02]"
              style={
                {
                  backgroundColor: color,
                  opacity: value > 0 ? 1 : 0.3,
                  "--target-width": `${width}%`,
                } as React.CSSProperties
              }
            />
          </div>
        </div>
        <div className="w-20 text-right">
          <div
            className={`text-sm font-medium transition-all duration-200 ${
              value > 0 ? "group-hover:font-bold" : "text-gray-400"
            }`}
            style={{ color: value > 0 ? color : undefined }}
          >
            {value}
          </div>
          <div className="text-xs text-gray-500 group-hover:text-gray-700 transition-all duration-200">
            {percentage}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ComparisonBarsContainerProps {
  data: Array<{ label: string; value: number }>;
  onSegmentClick?: (segmentValue: string) => void;
}

export function ComparisonBarsContainer({
  data,
  onSegmentClick,
}: ComparisonBarsContainerProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Sort data by value (descending) and then alphabetically
  const sortedData = [...data].sort((a, b) => {
    if (b.value !== a.value) {
      return b.value - a.value;
    }
    return a.label.localeCompare(b.label);
  });

  return (
    <>
      <style>
        {`
          @keyframes widthAnimation {
            from { width: 0; }
            to { width: var(--target-width); }
          }
          .animate-width {
            animation: widthAnimation 0.5s ease-out forwards;
          }
        `}
      </style>
      <div className="py-8 px-6">
        {sortedData.map((item, index) => (
          <ComparisonBar
            key={item.label}
            label={item.label}
            value={item.value}
            total={total}
            color={
              chartConstants.coloursArray[
                index % chartConstants.coloursArray.length
              ]
            }
            onClick={() => onSegmentClick?.(item.label)}
            isClickable={item.value > 0}
          />
        ))}
      </div>
    </>
  );
}
